import { Button, ButtonGroup, Checkbox, Dialog, DialogActions, DialogContent, FormControl, TextField, Typography } from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useCallback, useState } from "react";

import { AjaxResult } from "../../../enums/ajaxResult";
import { TimeZone } from "../../../enums/timeZone";
import { FormSection } from "../../shared/formSection";
import { FormField } from "../../shared/formField";
import { useApi } from "../../useApi";
import { ValidationErrors } from "../../shared/validationErrors";

import { DEFAULT_SITE_CONFIGURATION } from "./consts";
import { TimeZoneSelect } from "./TimeZoneSelect";

const missingGuidError = "Missing Service GUID";
const missingTokenError = "Missing Commissioning token";
const missingOrgIdError = "Missing Organization ID";
const missingExistingSiteError = "Select the site this device is replacing";

export type CommissionMode = "create" | "rebind";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSiteAdded: () => void;
    serviceGuidFromParams: string | undefined;
    commissioningTokenFromQuery: string | undefined;
    orgId: number | undefined;
    mode: CommissionMode;
    onModeChange: (mode: CommissionMode) => void;
    existingSiteId: number | undefined;
}

export function AddSiteDialog({
    isOpen,
    onClose,
    onSiteAdded,
    serviceGuidFromParams,
    commissioningTokenFromQuery,
    orgId,
    mode,
    onModeChange,
    existingSiteId,
    children,
}: React.PropsWithChildren<Props>) {
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [siteName, setSiteName] = useState("");
    const [timeZone, setTimeZone] = useState<TimeZone>(TimeZone.UTC);
    const [isActive, setIsActive] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);

    const handleSiteNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSiteName(event.currentTarget.value)
    }, []);

    function saveNewSite() {
        setErrors([]);
        if (!serviceGuidFromParams) {
            setErrors([missingGuidError]);
            return;
        }
        if (!commissioningTokenFromQuery) {
            setErrors([missingTokenError]);
            return;
        }
        if (!orgId) {
            setErrors([missingOrgIdError]);
            return;
        }
        setLoading(true);
        siteApi
            .post({
                organizationId: orgId,
                name: siteName,
                emailAddresses: "",
                serviceGuid: serviceGuidFromParams,
                commissioningToken: commissioningTokenFromQuery,
                excludeFromBilling: false,
                emailAlertsEnabled: true,
                referenceSiteId: "",
                timeZone: timeZone,
                maintenanceWindow: null,
                siteConfiguration: DEFAULT_SITE_CONFIGURATION,
                notes: null,
                isActive: isActive,
            })
            .then((r) => {
                if (r.result === AjaxResult.Success) {
                    onSiteAdded();
                    setSiteName("");
                    onClose();
                    enqueueSnackbar("Site created");
                } else if (r.messages) {
                    setErrors(r.messages);
                } else {
                    enqueueSnackbar("ERROR: Failed to create site");
                }
                setLoading(false);
            });
    }

    function rebindService() {
        setErrors([]);
        if (!serviceGuidFromParams) {
            setErrors([missingGuidError]);
            return;
        }
        if (!commissioningTokenFromQuery) {
            setErrors([missingTokenError]);
            return;
        }
        if (!existingSiteId) {
            setErrors([missingExistingSiteError]);
            return;
        }
        setLoading(true);
        siteApi
            .rebindService(existingSiteId, serviceGuidFromParams, commissioningTokenFromQuery)
            .then((r) => {
                if (r.result === AjaxResult.Success) {
                    onSiteAdded();
                    onClose();
                    enqueueSnackbar("Device replaced — the site will sync to it automatically");
                } else if (r.messages) {
                    setErrors(r.messages);
                } else {
                    enqueueSnackbar("ERROR: Failed to replace the device");
                }
                setLoading(false);
            });
    }

    const isRebindMode = mode === "rebind";

    return (
        <Dialog open={isOpen} disablePortal>
            <DialogContent style={{ minWidth: "400px" }}>
                <FormSection headerText={isRebindMode ? "Replace Device on Existing Site" : "Create New Site"}>
                    <ButtonGroup style={{ marginBottom: "16px" }}>
                        <Button
                            variant={mode === "create" ? "contained" : "outlined"}
                            color="primary"
                            onClick={() => onModeChange("create")}
                        >
                            Create new site
                        </Button>
                        <Button
                            variant={isRebindMode ? "contained" : "outlined"}
                            color="primary"
                            onClick={() => onModeChange("rebind")}
                        >
                            Replace device on existing site
                        </Button>
                    </ButtonGroup>

                    {children}

                    <FormField label="Service Id" required={true}>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={serviceGuidFromParams}
                                disabled={true}
                                variant="outlined"
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>

                    {isRebindMode ? (
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: "8px" }}>
                            This device will take over the site selected above. The old device will be marked
                            replaced, and the site's existing configuration will download to this device
                            automatically.
                        </Typography>
                    ) : (
                        <>
                            <FormField label="Site Name" required={true}>
                                <FormControl style={{ flexGrow: 1 }}>
                                    <TextField
                                        value={siteName}
                                        onChange={handleSiteNameChange}
                                        variant="outlined"
                                        InputProps={{ inputProps: { style: { padding: "8px" } } }}
                                    />
                                </FormControl>
                            </FormField>

                            <FormField label="Time zone" required={true}>
                                <FormControl style={{ flexGrow: 1 }}>
                                    <TimeZoneSelect
                                        value={timeZone}
                                        onChange={setTimeZone}
                                    />
                                </FormControl>
                            </FormField>

                            <FormField label="Deactivated">
                                <Checkbox
                                    checked={!isActive}
                                    color="primary"
                                    onChange={(event) => {
                                        setIsActive(!event.target.checked);
                                    }}
                                />
                            </FormField>
                        </>
                    )}

                    <ValidationErrors errors={errors} />
                </FormSection>
            </DialogContent>

            <DialogActions>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={isRebindMode ? rebindService : saveNewSite}
                    disabled={loading || (isRebindMode ? !existingSiteId : !orgId || !siteName)}
                >
                    {loading ? "Saving..." : isRebindMode ? "Replace Device" : "Save"}
                </Button>

                <Button
                    color="secondary"
                    variant="contained"
                    onClick={onClose}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
